// Added by Sejune Oh on 2019-06-25
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { FC, RefObject } from 'react'
import { Theme, createStyles, WithStyles, WithTheme, withStyles } from '@material-ui/core'
import moment from 'moment'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import Intl from '@/helper/Intl'
import {
    UserAccessLevelProps,
    UILanguageProps,
    ReactSelectEvent,
    ReactChangeWithValue,
    MgmtValidator,
    ReactFormEvent,
    FormatterProps,
} from '@/helper/define'
import { checkNotNumberSpecialCharacter } from '@/helper/util'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Typography } from '@/components/_primes'
import MgmtComponentFormStyle from '@/styles/management/MgmtComponentFormStyle'
import Input from '@/components/common/Input'
import Select from '@/components/common/Select'
import { FormControl, FormControlLabel, FormLabel, RadioGroup, Radio } from '@/components/_primes'
import { UserElementProps } from '@/stores/mgmt/user'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface UserFormFCProps extends WithStyles<typeof styles>, WithTheme, MgmtValidator, FormatterProps {
    userElement: UserElementProps
    accessLevelList: UserAccessLevelProps[]
    uiLanguageList: UILanguageProps[]
    usernameRef: RefObject<any>
    emailRef: RefObject<any>
    pwdRef: RefObject<any>
    confirmPwdRef: RefObject<any>
    uiLanguage: UILanguageProps
    accessLevel: UserAccessLevelProps
    onUILanguageChange: ReactSelectEvent
    onAccessLevelChange: ReactSelectEvent
    formSubmitBtnRef: RefObject<HTMLButtonElement>
    formId: string
    isActivated: string
    onRadioChange: ReactChangeWithValue
    onSubmit: ReactFormEvent
    requiredSysAdminuser: boolean
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const UserForm: FC<UserFormFCProps> = ({ classes, validator, blurValidator, formatter, ...props }) => {
    const title = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.title_user_form' })
    const labelExpiredAt = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.label_expired_at' })
    const labelUserName = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.label_user_name' })
    const labelDispName = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.label_disp_name' })
    const labelEmail = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.label_email' })
    const labelPwd = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.label_password' })
    const labelConfirmPwd = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.label_confirm_password' })
    const labelAccessLevel = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.label_access_level' })
    const labelUiLang = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.label_ui_lang' })
    const labelDesc = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.label_desc' })
    const subTitleEnable = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.sub_title_enable' })
    const labelEnabled = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.label_enabled' })
    const labelDisabled = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.label_disabled' })
    const descExpiredAt = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.desc_expired_at' })
    const conditionExpiredAt = Intl.getIntlText({ id: 'htmljs_react.mgmt.userform.condition_expired_at' })

    const isUpdate = typeof props.userElement!.id !== 'undefined'

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // handler
    const getExpiredAtDefaultValue = (value: string) => {
        if (value === '0001-01-01T00:00:00Z' || !value) return '0'
        return moment(value).format('YYYYMMDD')
    }

    return (
        <div className={classes.FormWrapper}>
            <div className={classes.TextWrapper}>
                <Typography variant="h1" className={classes.FormTtitle}>
                    {title}
                </Typography>
            </div>
            <div className={classes.InputWrapper}>
                <form id={props.formId} onSubmit={props.onSubmit}>
                    <Input
                        ref={props.usernameRef}
                        id={'username'}
                        type="string"
                        label={labelUserName}
                        required={true}
                        validator={validator!.userNameValidator}
                        blurValidator={blurValidator!.userNameBlurValidator}
                        defaultValue={props.userElement!.username}
                        disabled={isUpdate}
                        autoFocus={!isUpdate}
                        inputProps={{
                            'data-cy-input': 'username',
                        }}
                    />
                    <Input
                        id={'display_name'}
                        type="string"
                        label={labelDispName}
                        required={true}
                        validator={validator!.displayNameValidator}
                        defaultValue={props.userElement!.display_name}
                        autoFocus={isUpdate}
                        inputProps={{
                            'data-cy-input': 'display_name',
                        }}
                    />
                    <Input
                        ref={props.emailRef}
                        id={'email'}
                        type="string"
                        label={labelEmail}
                        isFullWidth={true}
                        required={true}
                        validator={validator!.emailValidator}
                        blurValidator={blurValidator!.emailBlurValidator}
                        defaultValue={props.userElement!.email}
                        inputProps={{
                            'data-cy-input': 'email',
                        }}
                    />
                    {!isUpdate && (
                        <>
                            <Input
                                ref={props.pwdRef}
                                id={'password'}
                                label={labelPwd}
                                required={true}
                                isPassword={true}
                                validator={validator!.pwdValidator}
                                blurValidator={blurValidator!.pwdBlurValidator}
                                data-cy-input="password"
                            />
                            <Input
                                ref={props.confirmPwdRef}
                                id={'confirm_pwd'}
                                label={labelConfirmPwd}
                                required={true}
                                isPassword={true}
                                validator={validator!.confirmPwdValidator}
                                blurValidator={blurValidator!.confirmPwdBlurValidator}
                                data-cy-input="confirm_pwd"
                            />
                        </>
                    )}
                    <Select
                        id="access_level"
                        label={labelAccessLevel}
                        value={props.accessLevel!.enumValue}
                        keyProps={'enumValue'}
                        menuItems={props.accessLevelList}
                        onChange={props.onAccessLevelChange}
                        isRequired={true}
                        itemFormatter={formatter!.accessLevelFormatter}
                        disabled={props.requiredSysAdminuser}
                    />
                    <Input
                        id="expired_at"
                        label={labelExpiredAt}
                        type={'number'}
                        onKeyDown={checkNotNumberSpecialCharacter}
                        description={descExpiredAt}
                        condition={conditionExpiredAt}
                        validator={validator!.dateValidator}
                        blurValidator={blurValidator!.dateBlurValidator}
                        defaultValue={getExpiredAtDefaultValue(props.userElement!.expired_at!)}
                        disabled={props.requiredSysAdminuser} // 최초 admin의 경우에는 변경하지 못하도록 한다
                        required
                    />
                    <Select
                        id="ui_language"
                        label={labelUiLang}
                        value={props.uiLanguage!.id}
                        menuItems={props.uiLanguageList}
                        onChange={props.onUILanguageChange}
                        isRequired={true}
                        itemFormatter={formatter!.uiLanguageFormatter}
                    />
                    {!isUpdate && (
                        <FormControl className={classes.RadioBtnWrapper}>
                            <FormLabel>{subTitleEnable}</FormLabel>
                            <RadioGroup
                                className={classes.RadioBtnGroupWrapper}
                                aria-label="actiavated"
                                name="isActivated"
                                value={props.isActivated}
                                onChange={props.onRadioChange}
                            >
                                <FormControlLabel
                                    value="yes"
                                    control={<Radio color="primary" />}
                                    label={labelEnabled}
                                    labelPlacement="end"
                                />
                                <FormControlLabel
                                    value="no"
                                    control={<Radio color="primary" />}
                                    label={labelDisabled}
                                    labelPlacement="end"
                                />
                            </RadioGroup>
                        </FormControl>
                    )}
                    <Input
                        id={'description'}
                        type="string"
                        label={labelDesc}
                        isFullWidth={true}
                        validator={validator!.descValidator}
                        defaultValue={props.userElement!.description}
                        inputProps={{
                            'data-cy-input': 'description',
                        }}
                    />
                    <button ref={props.formSubmitBtnRef} type="submit" hidden />
                </form>
            </div>
        </div>
    )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const styles = (theme: Theme) =>
    createStyles({
        ...MgmtComponentFormStyle(theme),
    })

export default withStyles(styles, { withTheme: true })(UserForm)
